import { EQAuthParamType } from 'src/Types';
import { Response, Select2WebElementType } from 'src/Types/ReDoc';
import { EQueueConfigType } from 'src/Types/ReDoc/EQueue';
import {
	ReturnQueueCountType,
	ReturnQueueItemType,
	ReturnStateType,
} from 'src/Types/ReDoc/Return';

export default class ReturnAPI {
	static _urls = {
		/** Загрузка конфигурации очереди */
		loadConfig: '/Nvx.ReDoc.ReturnEQ/LoadConfig',
		saveConfig: '/Nvx.ReDoc.ReturnEQ/SaveConfig',
		start: '/Nvx.ReDoc.ReturnEQ/Start',
		/** Получение текущего состояния электронной очереди */
		pultStatus: '/Nvx.ReDoc.ReturnEQ/PultStatus',
		/** Получение состояния сводных счетчиков очереди */
		getQueueState: '/Nvx.ReDoc.ReturnEQ/QueueCount',
		queue: '/Nvx.ReDoc.ReturnEQ/Queue',
		call: '/Nvx.ReDoc.ReturnEQ/InviteNext',
		callById: '/Nvx.ReDoc.ReturnEQ/InviteById',
		beginServing: '/Nvx.ReDoc.ReturnEQ/BeginServing',
		finish: '/Nvx.ReDoc.ReturnEQ/Finish',
		transfer: '/Nvx.ReDoc.ReturnEQ/Transfer',
		windows: '/Nvx.ReDoc.ReturnEQ/Windows?transfer=true',
		beginBreak: '/Nvx.ReDoc.ReturnEQ/BeginBreak',
		endBreak: '/Nvx.ReDoc.ReturnEQ/EndBreak',
		reject: '/Nvx.ReDoc.ReturnEQ/Reject',
	};

	/** Получение конфигурации ЭО */
	static getConfig = async (): Promise<Response<EQueueConfigType>> => {
		try {
			const url = window.host + this._urls.loadConfig;
			const request = await fetch(url, { credentials: 'include' });
			if (request.status === 200) {
				const response = (await request.json()) as Response<{
					windowIp?: string;
				}>;
				return {
					...response,
					result: {
						contactId: '',
						login: '',
						password: '',
						officeId: -1,
						pultId: response.result?.windowIp ?? '',
						pultName: null,
						queueServerAddress: null,
					},
				};
			}
			throw new Error('Ошибка при получении настроек ЭО.');
		} catch (e) {
			console.warn(e);
			throw new Error('Ошибка при получении настроек ЭО.');
		}
	};

	/** Сохранение конфигурации ЭО */
	static setConfig = async (
		params: EQAuthParamType,
	): Promise<Response<null>> => {
		const frmData = new FormData();
		frmData.append('rt', 'dfd');
		frmData.append('windowIp', params.pultId);
		try {
			const url = window.host + this._urls.saveConfig;
			const request = await fetch(url, {
				method: 'POST',
				credentials: 'include',
				body: frmData,
			});
			if (request.status === 200) {
				return await request.json();
			}
			throw new Error('Ошибка при сохранении настроек ЭО.');
		} catch (e) {
			console.warn(e);
			throw new Error('Ошибка при сохранении настроек ЭО.');
		}
	};

	/** Старт сессии ЭО */
	static start = async (id: string): Promise<Response<ReturnStateType>> => {
		try {
			const url = window.host + this._urls.start;
			const frmData = new FormData();
			frmData.append('id', id);
			const request = await fetch(url, {
				method: 'POST',
				credentials: 'include',
				body: frmData,
			});
			if (request.status === 200) {
				return await request.json();
			}
			throw new Error('Ошибка входа в электронную очередь.');
		} catch (e) {
			console.warn(e);
			throw new Error('Ошибка входа в электронную очередь.');
		}
	};

	/** Получение текущего состояния электронной очереди */
	static getStatus = async (): Promise<Response<ReturnStateType>> => {
		try {
			const url = window.host + this._urls.pultStatus;
			const request = await fetch(url, {
				method: 'GET',
				credentials: 'include',
			});
			if (request.status === 200) {
				return await request.json();
			}
			throw new Error('Ошибка получения статуса ЭО.');
		} catch (e) {
			console.warn(e);
			throw new Error('Ошибка получения статуса ЭО.');
		}
	};

	/** Запрос на получение состояния сводных счетчиков очереди */
	static getQueueState = async (): Promise<
		Response<ReturnQueueCountType>
	> => {
		try {
			const url = window.host + this._urls.getQueueState;
			const request = await fetch(url, {
				method: 'GET',
				credentials: 'include',
			});
			if (request.status === 200) {
				return await request.json();
			}
			throw new Error('Ошибка получения состояния очереди ЭО.');
		} catch (e) {
			console.warn(e);
			throw new Error('Ошибка получения состояния очереди ЭО.');
		}
	};

	/** Получение общей очереди */
	static getQueue = async (
		term: string = '',
	): Promise<Response<ReturnQueueItemType[]>> => {
		try {
			const params = new URLSearchParams();
			if (term.trim()) {
				params.append('term', term);
			}
			const query = params.toString();
			const url = `${window.host}${this._urls.queue}${query ? `?${query}` : ''}`;
			const request = await fetch(url, {
				method: 'GET',
				credentials: 'include',
			});
			if (request.status === 200) {
				return await request.json();
			}
			throw new Error('Ошибка получения очереди.');
		} catch (e) {
			console.warn(e);
			throw new Error('Ошибка получения очереди.');
		}
	};

	/** Вызов посетителя по идентификатору талона */
	static callById = async (
		ticketId: string,
	): Promise<Response<ReturnStateType>> => {
		try {
			const url = `${window.host}${this._urls.callById}?ticketId=${encodeURIComponent(ticketId)}`;
			const request = await fetch(url, {
				method: 'POST',
				credentials: 'include',
			});
			if (request.status === 200) {
				return await request.json();
			}
			throw new Error('Ошибка вызова посетителя по талону.');
		} catch (e) {
			console.warn(e);
			throw new Error('Ошибка вызова посетителя по талону.');
		}
	};

	/** Вызов следующего посетителя по очереди */
	static call = async (): Promise<Response<ReturnStateType>> => {
		try {
			const url = window.host + this._urls.call;
			const request = await fetch(url, {
				method: 'POST',
				credentials: 'include',
			});
			if (request.status === 200) {
				return await request.json();
			}
			throw new Error('Ошибка вызова следующего посетителя.');
		} catch (e) {
			console.warn(e);
			throw new Error('Ошибка вызова следующего посетителя.');
		}
	};

	/** Начало обслуживания текущего посетителя */
	static beginServing = async (): Promise<Response<ReturnStateType>> => {
		try {
			const url = window.host + this._urls.beginServing;
			const request = await fetch(url, {
				method: 'POST',
				credentials: 'include',
			});
			if (request.status === 200) {
				return await request.json();
			}
			throw new Error('Ошибка начала обслуживания посетителя.');
		} catch (e) {
			console.warn(e);
			throw new Error('Ошибка начала обслуживания посетителя.');
		}
	};

	/** Завершение сессии ЭО */
	static finish = async (): Promise<Response<ReturnStateType>> => {
		try {
			const url = window.host + this._urls.finish;
			const request = await fetch(url, {
				method: 'POST',
				credentials: 'include',
			});
			if (request.status === 200) {
				return await request.json();
			}
			throw new Error('Ошибка завершения сеанса ЭО.');
		} catch (e) {
			console.warn(e);
			throw new Error('Ошибка завершения сеанса ЭО.');
		}
	};

	/** Перенаправить посетителя в другое окно */
	static transfer = async (
		toWindow: string,
	): Promise<Response<ReturnStateType>> => {
		try {
			const url = `${window.host}${this._urls.transfer}?toWindow=${encodeURIComponent(toWindow)}`;
			const request = await fetch(url, {
				method: 'POST',
				credentials: 'include',
			});
			if (request.status === 200) {
				return await request.json();
			}
			throw new Error('Ошибка перенаправления посетителя.');
		} catch (e) {
			console.warn(e);
			throw new Error('Ошибка перенаправления посетителя.');
		}
	};

	/** Начало перерыва */
	static beginBreak = async (): Promise<Response<ReturnStateType>> => {
		try {
			const url = window.host + this._urls.beginBreak;
			const request = await fetch(url, {
				method: 'POST',
				credentials: 'include',
			});
			if (request.status === 200) {
				return await request.json();
			}
			throw new Error('Ошибка запроса на перерыв ЭО.');
		} catch (e) {
			console.warn(e);
			throw new Error('Ошибка запроса на перерыв ЭО.');
		}
	};

	/** Завершение перерыва */
	static endBreak = async (): Promise<Response<ReturnStateType>> => {
		try {
			const url = window.host + this._urls.endBreak;
			const request = await fetch(url, {
				method: 'POST',
				credentials: 'include',
			});
			if (request.status === 200) {
				return await request.json();
			}
			throw new Error('Ошибка запроса на окончание перерыва ЭО.');
		} catch (e) {
			console.warn(e);
			throw new Error('Ошибка запроса на окончание перерыва ЭО.');
		}
	};

	/** Неявка посетителя */
	static reject = async (): Promise<Response<ReturnStateType>> => {
		try {
			const url = window.host + this._urls.reject;
			const request = await fetch(url, {
				method: 'POST',
				credentials: 'include',
			});
			if (request.status === 200) {
				return await request.json();
			}
			throw new Error('Ошибка запроса неявки посетителя.');
		} catch (e) {
			console.warn(e);
			throw new Error('Ошибка запроса неявки посетителя.');
		}
	};

	/** Получение доступных окон для перенаправления */
	static getWindows = async (): Promise<Select2WebElementType[]> => {
		try {
			const url = window.host + this._urls.windows;
			const request = await fetch(url, {
				method: 'GET',
				credentials: 'include',
			});
			if (request.status === 200) {
				const data = await request.json();
				return Array.isArray(data) ? data : [];
			}
			throw new Error('Ошибка получения списка окон.');
		} catch (e) {
			console.warn(e);
			throw new Error('Ошибка получения списка окон.');
		}
	};
}
